import React, { useEffect, useState } from "react";
import "./WorkerLoginPage.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Globe, X } from "lucide-react"; 

/* ===== SVARBU: NAUJAS IMPORTAS (@capgo) ===== */
import { NativeBiometric } from "@capgo/capacitor-native-biometric";

/* ===== FIREBASE IMPORTAI ===== */
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";

/* ===== Importuojame konfigūraciją ===== */
import i18n from "../../i18n/i18n"; 

/* ===== Importuojame paveiksliukus ===== */
import bfLogo from "../../assets/bf_logo.png";
import faceIcon from "../../assets/faceid.png";
import fingerIcon from "../../assets/fingerprint.png";

const WorkerLoginPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [authAttempts, setAuthAttempts] = useState(0);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add("worker-login-page");
    
    const isRegistered = localStorage.getItem("workerDeviceRegistered") === "true";
    if (!isRegistered) {
      navigate("/worker", { replace: true });
    }

    return () => document.body.classList.remove("worker-login-page");
  }, [navigate]);

  const handleFinalLockout = () => {
    // Pakeista: jokio lietuviško teksto, tik vertimo raktas
    alert(t('lockout_message'));
    localStorage.clear(); 
    navigate("/worker", { replace: true });
  };

  const handleAuth = async () => {
    try {
      // 1. Patikriname, ar įrenginys palaiko biometriją
      const result = await NativeBiometric.isAvailable();
      
      if (!result.isAvailable) {
        // Pakeista: jokio lietuviško teksto
        alert(t('biometrics_not_supported'));
        return;
      }

      // 2. Iškviečiame biometriją su vertimais
      await NativeBiometric.verifyIdentity({
        reason: "BuildForce AL",
        title: t('biometric_title'),             // Buvo hardcoded
        subtitle: t('biometric_subtitle'),       // Buvo hardcoded
        description: t('biometric_description'), // Buvo hardcoded
      });

      // 3. Jei patikra sėkminga - tęsiame
      const storedWorkerId = localStorage.getItem("workerId");

      if (!storedWorkerId) {
          console.error("Critical: workerId missing.");
          handleFinalLockout(); 
          return;
      }

      // Tikriname DB
      const workerSnap = await getDoc(doc(db, "workers", storedWorkerId));
      
      if (workerSnap.exists()) {
          const data = workerSnap.data();
          if (data.archived) {
              alert(t('account_archived')); // Pakeista
              return;
          }
      }

      // Viskas gerai -> įleidžiame
      localStorage.setItem("workerGateDone", "true");
      navigate("/worker/home", { replace: true });

    } catch (error) {
      console.error("Biometric error:", error);

      // Patikriname, ar vartotojas pats atšaukė
      if (error.code === "10" || error.message?.includes("cancel") || error.message?.includes("User")) {
        return; 
      }

      // Pakeista: klaidos pranešimas dabar naudoja vertimą
      // Pvz.: "Error: Something went wrong"
      alert(`${t('error_general')}: ${error.message || "Unknown error"}`);

      const nextCount = authAttempts + 1;
      if (nextCount >= 5) {
        handleFinalLockout();
      } else {
        setAuthAttempts(nextCount);
      }
    }
  };

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    setIsLangModalOpen(false);
  };

  const languages = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
    { code: "lt", label: "Lietuvių", flag: "🇱🇹" },
    { code: "ru", label: "Русский", flag: "🇷🇺" },
    { code: "ro", label: "Română", flag: "🇷🇴" },
    { code: "bg", label: "Българski", flag: "🇧🇬" },
    { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  ];

  return (
    <div className="worker-login-container">
      <div className="worker-login-card gold-aura">
        <img src={bfLogo} alt="BF Logo" className="worker-login-logo" />
        
        <h1 className="worker-login-title">BuildForce AL</h1>
        <p className="worker-login-subtitle">{t('biometric_login')}</p>

        <div className="login-actions-group">
            <button className="worker-login-btn biometric-btn-glow" onClick={handleAuth}>
              <div className="btn-icon-wrapper dual-icons">
                <img src={faceIcon} className="icon-img-active pulse-blue" alt="FaceID" />
                <img src={fingerIcon} className="icon-img-active pulse-gold" alt="Fingerprint" />
              </div>
              {/* Jei vertimo nėra, rodys anglišką default */}
              <span className="btn-text">{t('face_fingerprint_id')}</span>
              {authAttempts > 0 && <span className="badge-count">{authAttempts}/5</span>}
            </button>

            <button className="worker-login-btn lang-btn-glow" onClick={() => setIsLangModalOpen(true)}>
              <div className="btn-icon-wrapper">
                <Globe size={24} className="blue-globe" />
              </div>
              <span className="btn-text">{t('language')}</span>
            </button>
        </div>

        {authAttempts > 2 && (
            <div className="security-notice">
                <AlertTriangle size={20} /> {t('multiple_failed_attempts')}
            </div>
        )}
      </div>

      {isLangModalOpen && (
        <div className="lang-modal-overlay" onClick={() => setIsLangModalOpen(false)}>
          <div className="lang-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('select_language')}</h3>
              <X className="close-modal" onClick={() => setIsLangModalOpen(false)} />
            </div>
            <div className="lang-list">
              {languages.map((lang) => (
                <div 
                  key={lang.code} 
                  className={`lang-item ${i18n.language === lang.code ? 'active-lang' : ''}`}
                  onClick={() => changeLang(lang.code)}
                >
                  <span className="lang-flag">{lang.flag}</span>
                  <span className="lang-name">{lang.label}</span>
                  {i18n.language === lang.code && <div className="active-dot" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerLoginPage;